import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ICheckout, ICheckoutItem, ICheckoutItemConfig } from '../interfaces/ICheckout';

type PreviewItemPayload = ICheckoutItem & {
  valueType?: string;
  openValueMinAmount?: number | string;
  openValueMaxAmount?: number | string;
};

function normalizeItemConfig(item: PreviewItemPayload): ICheckoutItemConfig | undefined {
  const isOpenValue = item.config?.isOpenValue === true;
  const valueTypeOpen = item.valueType === 'open';

  if (!isOpenValue && !valueTypeOpen) return item.config;

  const toCents = (v: unknown): number | undefined => {
    if (v == null) return undefined;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string') {
      const n = parseInt(String(v).replace(/\D/g, ''), 10);
      return Number.isNaN(n) ? undefined : n;
    }
    return undefined;
  };
  const existing = item.config ?? {};
  const min = existing.openValueMinAmount ?? toCents(item.openValueMinAmount);
  const max = existing.openValueMaxAmount ?? toCents(item.openValueMaxAmount);

  return {
    isOpenValue: true,
    openValueMinAmount: min,
    openValueMaxAmount: max,
  };
}

function normalizePreviewItems(items: ICheckout['items']): ICheckout['items'] {
  if (!items?.length) return items;
  return items.map((item, index) => {
    const config = normalizeItemConfig(item);
    const isOpenValue = config?.isOpenValue === true;
    return {
      ...item,
      id: item.id || `preview-item-${index}`,
      amount: isOpenValue && ['main', 'cart'].includes(item.type) ? 0 : item.amount,
      recurrenceChargesQuantity: item.recurrenceChargesQuantity ?? 1,
      recurrenceFrequency: item.recurrenceFrequency ?? 'month',
      recurrenceInterval: item.recurrenceInterval ?? 1,
      billingType: item.billingType ?? 'single',
      ...(config ? { config } : {}),
    } as ICheckoutItem;
  });
}

function normalizePreviewData(raw: ICheckout): ICheckout {
  return {
    ...raw,
    type: raw.type || 'checkout',
    items: normalizePreviewItems(raw.items || []),
  };
}

export const usePreview = (id: string, modeAs: string) => {
  const [searchParams] = useSearchParams();

  const [prewiewInit, setPreviewInit] = useState(false);
  const [prewiewData, setPreviewData] = useState<ICheckout>({
    id: 'preview',
    teamId: 'preview',
    items: [
      {
        id: '1',
        description: 'Produto 1',
        details: 'Detalhes do produto 1',
        amount: 100,
        quantity: 1,
        code: '1',
        image: 'https://via.placeholder.com/150',
        type: 'main',
        recurrenceChargesQuantity: 1,
        recurrenceFrequency: 'month',
        recurrenceInterval: 1,
        billingType: 'single',
      },
    ],
    allowCreditCard: true,
    allowBoleto: true,
    allowPix: true,
    askAddress: true,
    allowAddPayment: true,
    type: 'checkout',
  });

  useEffect(() => {
    if (modeAs != 'preview') {
      return;
    }

    const data = searchParams.get('data');
    if (data) {
      try {
        const parsedData = normalizePreviewData(JSON.parse(atob(data)) as ICheckout);
        setPreviewData(parsedData);
      } catch {
        setPreviewData((prev) => prev);
      }
      return;
    }

    const handler = (event: MessageEvent) => {
      if (event.data.type != 'preview') {
        return;
      }

      setPreviewData(
        normalizePreviewData({
          ...event.data.data,
          type: 'checkout',
        } as ICheckout)
      );
    };
    window.addEventListener('message', handler);

    setPreviewInit(true);

    return () => {
      window.removeEventListener('message', handler);
    };
  }, [modeAs, id, searchParams]);

  useEffect(() => {
    if (prewiewInit) {
      return;
    }

    window.parent.postMessage(
      { type: 'ready' },
      {
        targetOrigin: '*',
      }
    );
  }, [prewiewInit]);

  return {
    prewiewData,
  };
};
